import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useClientSegmentation } from '../hooks/useClients';

const { FiUsers, FiDollarSign, FiCalendar, FiTool, FiAlertCircle, FiTarget, FiTrendingUp } = FiIcons;

const ClientSegmentation = ({ clients }) => {
  const segmentationData = useClientSegmentation(clients);

  const getSegmentColor = (index) => {
    const colors = [
      'bg-blue-100 text-blue-800 border-blue-200',
      'bg-green-100 text-green-800 border-green-200',
      'bg-purple-100 text-purple-800 border-purple-200',
      'bg-orange-100 text-orange-800 border-orange-200',
      'bg-pink-100 text-pink-800 border-pink-200',
      'bg-indigo-100 text-indigo-800 border-indigo-200',
      'bg-yellow-100 text-yellow-800 border-yellow-200',
      'bg-red-100 text-red-800 border-red-200'
    ];
    return colors[index % colors.length];
  };

  const formatCurrency = (amount) => {
    return `$${amount.toLocaleString()}`;
  };

  const segmentCards = [
    {
      title: 'Service Type Distribution',
      icon: FiTool,
      data: segmentationData.serviceType,
      color: 'blue'
    },
    {
      title: 'Billing Frequency',
      icon: FiCalendar,
      data: segmentationData.billingFrequency,
      color: 'green'
    },
    {
      title: 'GST Classification',
      icon: FiDollarSign,
      data: segmentationData.gstType,
      color: 'purple'
    },
    {
      title: 'Payment Status',
      icon: FiAlertCircle,
      data: segmentationData.paymentStatus,
      color: 'orange'
    },
    {
      title: 'Account Managers',
      icon: FiUsers,
      data: segmentationData.accountManager,
      color: 'indigo'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="bg-white rounded-xl p-6 shadow-soft border border-gray-200"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-800 flex items-center">
            <SafeIcon icon={FiTarget} className="w-6 h-6 mr-2 text-blue-600" />
            Client Segmentation Analytics
          </h3>
          <p className="text-gray-600 mt-1">Comprehensive analysis of your client base</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-gray-800">{clients.length}</p>
          <p className="text-sm text-gray-600">Total Clients</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mb-8">
        {segmentCards.map((segment, cardIndex) => (
          <div key={segment.title} className={`bg-${segment.color}-50 rounded-lg p-4 border border-${segment.color}-200`}>
            <div className="flex items-center space-x-2 mb-3">
              <SafeIcon icon={segment.icon} className={`w-5 h-5 text-${segment.color}-600`} />
              <h4 className="font-semibold text-gray-700 text-sm">{segment.title}</h4>
            </div>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {Object.entries(segment.data).length > 0 ? (
                Object.entries(segment.data).map(([key, count], index) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${getSegmentColor(index)}`}>
                      {key.length > 12 ? `${key.substring(0, 12)}...` : key}
                    </span>
                    <span className="text-sm font-bold text-gray-800">{count}</span>
                  </div>
                ))
              ) : (
                <div className="text-xs text-gray-500 text-center py-2">No data available</div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Revenue Analysis */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 border border-green-200">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-bold text-gray-800 flex items-center">
            <SafeIcon icon={FiTrendingUp} className="w-5 h-5 mr-2 text-green-600" />
            Revenue Analysis by Service Type
          </h4>
          <div className="text-right">
            <p className="text-lg font-bold text-gray-800">
              {formatCurrency(Object.values(segmentationData.revenueByService).reduce((sum, revenue) => sum + revenue, 0))}
            </p>
            <p className="text-xs text-gray-600">Total Monthly Revenue</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {Object.entries(segmentationData.revenueByService).length > 0 ? (
            Object.entries(segmentationData.revenueByService).map(([service, revenue], index) => (
              <motion.div
                key={service}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="bg-white rounded-lg p-4 text-center border border-gray-200 hover:shadow-md transition-shadow duration-200"
              >
                <p className="text-xs font-semibold text-gray-600 mb-1">
                  {service.length > 8 ? `${service.substring(0, 8)}...` : service}
                </p>
                <p className="text-lg font-bold text-gray-800">{formatCurrency(revenue)}</p>
                <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                  <div
                    className="bg-gradient-to-r from-green-500 to-blue-500 h-1.5 rounded-full"
                    style={{
                      width: `${(revenue / Math.max(...Object.values(segmentationData.revenueByService))) * 100}%`
                    }}
                  ></div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="col-span-full text-center py-8 text-gray-500">
              <SafeIcon icon={FiDollarSign} className="w-12 h-12 mx-auto mb-2" />
              <p>No revenue data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
          <p className="text-2xl font-bold text-blue-800">
            {Object.values(segmentationData.serviceType).reduce((sum, count) => sum + count, 0)}
          </p>
          <p className="text-xs text-blue-600">Active Services</p>
        </div>
        
        <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
          <p className="text-2xl font-bold text-green-800">
            {segmentationData.paymentStatus['Active'] || 0}
          </p>
          <p className="text-xs text-green-600">Active Payments</p>
        </div>
        
        <div className="bg-orange-50 rounded-lg p-4 text-center border border-orange-200">
          <p className="text-2xl font-bold text-orange-800">
            {(segmentationData.paymentStatus['Overdue'] || 0) + (segmentationData.paymentStatus['Pending'] || 0)}
          </p>
          <p className="text-xs text-orange-600">Pending/Overdue</p>
        </div>
        
        <div className="bg-purple-50 rounded-lg p-4 text-center border border-purple-200">
          <p className="text-2xl font-bold text-purple-800">
            {Object.keys(segmentationData.accountManager).length}
          </p>
          <p className="text-xs text-purple-600">Account Managers</p>
        </div>
      </div>
    </motion.div>
  );
};

export default ClientSegmentation;